import { Check, X, LayoutGrid, Info } from "lucide-react";
import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const FeatureComparisonMatrix = () => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const featureCategories = [
    {
      category: "Core Platform",
      features: [
        { name: "Unlimited User Seats", minimums: true, standard: true, premier: true, tooltip: "Add as many team members as you need with no per-seat fees." },
        { name: "Client Sub-Accounts", minimums: "3", standard: "Unlimited", premier: "Unlimited", tooltip: "Manage multiple brands or client accounts from one dashboard." },
        { name: "Website & Funnel Builder", minimums: true, standard: true, premier: true, tooltip: "Drag-and-drop builder for websites, landing pages, and funnels." },
        { name: "CRM & Pipelines", minimums: true, standard: true, premier: true, tooltip: "Visual sales pipelines to track leads and deals." },
        { name: "Forms, Surveys & Quizzes", minimums: true, standard: true, premier: true, tooltip: "Create interactive forms to capture leads." }
      ]
    },
    {
      category: "Communication & Marketing",
      features: [
        { name: "Email + SMS Marketing", minimums: true, standard: true, premier: true, tooltip: "Send targeted email and SMS campaigns." },
        { name: "Unified Conversations", minimums: true, standard: true, premier: true, tooltip: "Manage all communications from one inbox." },
        { name: "Social Media Management", minimums: true, standard: true, premier: true, tooltip: "Schedule posts across platforms." },
        { name: "Monthly Credits", minimums: "$10", standard: "$25", premier: "$100", tooltip: "Complimentary monthly credits for emails and SMS." },
        { name: "Advanced Campaigns & A/B Testing", minimums: false, standard: true, premier: true, tooltip: "Test campaign variations to optimize results." }
      ]
    },
    {
      category: "AI & Automation",
      features: [
        { name: "Native AI Tools for Text Conversations", minimums: false, standard: true, premier: true, tooltip: "AI for conversations and text." },
        { name: "Native AI Tools for Phone Calls", minimums: false, standard: true, premier: true, tooltip: "AI for phone conversations." },
        { name: "Native AI Tools for Content", minimums: false, standard: true, premier: true, tooltip: "AI for content generation." },
        { name: "Native AI Tools for Review Management", minimums: false, standard: true, premier: true, tooltip: "AI for review responses." },
        { name: "Workflow Automation", minimums: true, standard: true, premier: true, tooltip: "Automate repetitive tasks and follow-ups." },
        { name: "Advanced Triggers & Webhooks", minimums: false, standard: true, premier: true, tooltip: "Complex automation rules and API connections." },
        { name: "AI Workflow Builder", minimums: false, standard: false, premier: true, tooltip: "AI generates automations from descriptions." },
        { name: "AI Funnel Builder", minimums: false, standard: false, premier: true, tooltip: "AI creates complete sales funnels." },
        { name: "AI Content Generation", minimums: false, standard: false, premier: true, tooltip: "Generate blogs, social content, and emails." }
      ]
    },
    {
      category: "Advanced Features",
      features: [
        { name: "Booking Calendars", minimums: false, standard: true, premier: true, tooltip: "Clients book appointments directly." },
        { name: "Communities", minimums: false, standard: true, premier: true, tooltip: "Build membership sites and community spaces." },
        { name: "Courses", minimums: false, standard: true, premier: true, tooltip: "Create and sell online courses." },
        { name: "Payments & Invoicing", minimums: true, standard: true, premier: true, tooltip: "Accept payments with Stripe integration." },
        { name: "Text to Pay", minimums: false, standard: true, premier: true, tooltip: "Send payment requests via text message." },
        { name: "Grey Labeled Mobile App", minimums: false, standard: true, premier: true, tooltip: "White-label mobile app for your business." },
        { name: "Online Reputation Management", minimums: false, standard: true, premier: true, tooltip: "Monitor and respond to reviews." },
        { name: "Google Business Profile Call Tracking", minimums: false, standard: true, premier: true, tooltip: "Track calls from your Google Business Profile." },
        { name: "Google Business Profile Optimisation", minimums: false, standard: true, premier: true, tooltip: "Optimize your Google Business Profile for visibility." },
        { name: "Facebook Messenger Integration", minimums: false, standard: true, premier: true, tooltip: "Connect and manage Facebook Messenger conversations." },
        { name: "Web Page Building", minimums: false, standard: true, premier: true, tooltip: "Build custom web pages with drag-and-drop." },
        { name: "Advanced Reporting", minimums: false, standard: true, premier: true, tooltip: "Custom dashboards and attribution tracking." }
      ]
    },
    {
      category: "Enterprise & Agency",
      features: [
        { name: "API Access", minimums: "Basic", standard: "Basic", premier: "Advanced", tooltip: "Connect to external systems via API." },
        { name: "SaaS Mode (White-label & Resell)", minimums: false, standard: false, premier: true, tooltip: "Rebrand and resell the platform." },
        { name: "Rebill Phone & Email with Markup", minimums: false, standard: false, premier: true, tooltip: "Add markup to communication costs." },
        { name: "Automated Account Reporting", minimums: false, standard: false, premier: true, tooltip: "Auto-generate and send client reports." },
        { name: "Custom Branding & White-labeling", minimums: false, standard: false, premier: true, tooltip: "Full platform customization." }
      ]
    },
    {
      category: "Support & Training",
      features: [
        { name: "24/7 Support", minimums: true, standard: true, premier: true, tooltip: "Round-the-clock support via chat and email." },
        { name: "1:1 Onboarding", minimums: true, standard: true, premier: true, tooltip: "Personalized onboarding session." },
        { name: "Group Training", minimums: "10 weeks", standard: "10 weeks", premier: "10 weeks", tooltip: "Live group training sessions." },
        { name: "Dedicated Success Manager", minimums: false, standard: true, premier: true, tooltip: "Your own success manager." },
        { name: "Private Slack Channel", minimums: false, standard: false, premier: true, tooltip: "Direct team access via Slack." }
      ]
    }
  ];

  const displayedCategories = showAllFeatures ? featureCategories : featureCategories.slice(0, 3);

  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === "boolean") {
      return value ? (
        <div 
          className="w-5 h-5 rounded-full flex items-center justify-center mx-auto"
          style={{ backgroundColor: isDark ? '#FFFFFF' : '#111111' }}
        >
          <Check className="h-3 w-3" strokeWidth={3} style={{ color: isDark ? '#111111' : '#FFFFFF' }} />
        </div>
      ) : (
        <X className="h-5 w-5 mx-auto" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#CCCCCC' }} />
      );
    }
    return <span className="text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{value}</span>;
  };

  // Theme colors
  const bgColor = isDark ? '#111111' : '#FFFFFF';
  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#E0E0E0';
  const headerBg = isDark ? 'rgba(255,255,255,0.05)' : '#F8F8F8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.03)' : '#FAFAFA';
  const textPrimary = isDark ? '#FFFFFF' : '#111111';
  const textSecondary = isDark ? '#AAAAAA' : '#555555';
  const popularColumnBg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(17,17,17,0.02)';

  return (
    <section className="pt-16 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div className="relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full overflow-hidden">
            {/* Animated border trace - uses global CSS animation */}
            <div className="absolute inset-0 rounded-full badge-animated-border" />
            {/* Static border */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}` }}
            />
            <div 
              className="absolute inset-0 rounded-full"
              style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}` }}
            />
            <div 
              className="absolute inset-[1px] rounded-full"
              style={{ backgroundColor: isDark ? '#1A1A1A' : '#FCFCF9' }}
            />
            <LayoutGrid className="relative z-10 w-3 h-3" style={{ color: textSecondary }} />
            <span className="relative z-10 text-xs font-medium" style={{ color: textPrimary }}>Compare</span>
          </div>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-3" style={{ color: textPrimary }}>
            Feature Comparison.
          </h2>
          <p className="text-sm max-w-2xl mx-auto" style={{ color: textSecondary }}>
            Compare features across all plans to find the perfect fit for your business.
          </p>
        </div>

        <TooltipProvider>
          {/* Desktop Table */}
          <div 
            className="hidden lg:block overflow-x-auto rounded-xl"
            style={{ 
              backgroundColor: cardBg, 
              border: `1px solid ${borderColor}`,
              boxShadow: isDark 
                ? '0 25px 50px -12px rgba(0,0,0,0.5)' 
                : '0 25px 50px -12px rgba(0,0,0,0.15)'
            }}
          >
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                  <th className="text-left p-4 text-sm font-semibold" style={{ color: textPrimary, width: '40%' }}>
                    Features
                  </th>
                  <th className="text-center p-4 text-sm font-semibold" style={{ color: textPrimary, width: '20%', borderLeft: `1px solid ${borderColor}` }}>
                    <div>Minimums</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{ color: textSecondary }}>The essential infrastructure</div>
                    <div className="text-xs font-normal mt-1" style={{ color: textSecondary }}>$197/mo</div>
                  </th>
                  <th className="text-center p-4 text-sm font-semibold" style={{ backgroundColor: popularColumnBg, color: textPrimary, width: '20%', borderLeft: `1px solid ${borderColor}` }}>
                    <div>Professional</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{ color: textSecondary }}>The professional business benchmark</div>
                    <div className="text-xs font-normal mt-1" style={{ color: textSecondary }}>$297/mo</div>
                    <div className="text-[10px] font-semibold mt-1 uppercase tracking-wider" style={{ color: textPrimary }}>Most Popular</div>
                  </th>
                  <th className="text-center p-4 text-sm font-semibold" style={{ color: textPrimary, width: '20%', borderLeft: `1px solid ${borderColor}` }}>
                    <div>Enterprise</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{ color: textSecondary }}>The elite, AI-driven executive suite</div>
                    <div className="text-xs font-normal mt-1" style={{ color: textSecondary }}>$697/mo</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedCategories.map((category, categoryIndex) => (
                  <>
                    <tr key={`category-${categoryIndex}`} style={{ backgroundColor: headerBg }}>
                      <td colSpan={4} className="p-3 text-sm font-semibold" style={{ color: textPrimary }}>
                        {category.category}
                      </td>
                    </tr>
                    {category.features.map((feature, featureIndex) => (
                      <tr 
                        key={`feature-${categoryIndex}-${featureIndex}`}
                        className="transition-colors cursor-default"
                        style={{ borderBottom: `1px solid ${borderColor}` }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td className="p-4 text-sm" style={{ color: textPrimary }}>
                          <div className="flex items-center gap-2">
                            <span>{feature.name}</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 cursor-help" style={{ color: '#888888' }} />
                              </TooltipTrigger>
                              <TooltipContent 
                                className="max-w-xs z-50"
                                style={{ backgroundColor: isDark ? '#2A2A2A' : '#FFFFFF', color: textPrimary, border: `1px solid ${borderColor}` }}
                              >
                                <p className="text-sm">{feature.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </td>
                        <td className="p-4 text-center" style={{ borderLeft: `1px solid ${borderColor}` }}>
                          {renderFeatureValue(feature.minimums)}
                        </td>
                        <td className="p-4 text-center" style={{ backgroundColor: popularColumnBg, borderLeft: `1px solid ${borderColor}` }}>
                          {renderFeatureValue(feature.standard)}
                        </td>
                        <td className="p-4 text-center" style={{ borderLeft: `1px solid ${borderColor}` }}>
                          {renderFeatureValue(feature.premier)}
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Accordion */}
          <div className="lg:hidden space-y-4">
            {displayedCategories.map((category, categoryIndex) => (
              <div 
                key={categoryIndex} 
                className="rounded-xl overflow-hidden"
                style={{ 
                  backgroundColor: cardBg, 
                  border: `1px solid ${borderColor}`,
                  boxShadow: isDark ? '0 10px 25px -5px rgba(0,0,0,0.3)' : '0 10px 25px -5px rgba(0,0,0,0.1)'
                }}
              >
                <div className="p-4" style={{ backgroundColor: headerBg }}>
                  <h3 className="text-sm font-semibold" style={{ color: textPrimary }}>{category.category}</h3>
                </div>
                <div>
                  {category.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex} 
                      className="p-4"
                      style={{ borderBottom: featureIndex < category.features.length - 1 ? `1px solid ${borderColor}` : 'none' }}
                    >
                      <div className="flex items-center gap-2 font-medium text-sm mb-3" style={{ color: textPrimary }}>
                        <span>{feature.name}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4" style={{ color: '#888888' }} />
                          </TooltipTrigger>
                          <TooltipContent 
                            className="max-w-xs z-50"
                            style={{ backgroundColor: isDark ? '#2A2A2A' : '#FFFFFF', color: textPrimary, border: `1px solid ${borderColor}` }}
                          >
                            <p className="text-sm">{feature.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 rounded" style={{ border: `1px solid ${borderColor}` }}>
                          <div className="mb-1" style={{ color: textSecondary }}>Minimums</div>
                          {renderFeatureValue(feature.minimums)}
                        </div>
                        <div className="text-center rounded p-2" style={{ backgroundColor: popularColumnBg, border: `1px solid ${borderColor}` }}>
                          <div className="mb-1" style={{ color: textSecondary }}>Professional</div>
                          {renderFeatureValue(feature.standard)}
                        </div>
                        <div className="text-center p-2 rounded" style={{ border: `1px solid ${borderColor}` }}>
                          <div className="mb-1" style={{ color: textSecondary }}>Enterprise</div>
                          {renderFeatureValue(feature.premier)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TooltipProvider>

        {/* Show More/Less Button */}
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="px-6 py-3 rounded-lg text-sm font-semibold hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            style={{ backgroundColor: isDark ? '#FFFFFF' : '#111111', color: isDark ? '#111111' : '#FFFFFF' }}
          >
            {showAllFeatures ? "Show Less Features" : "Show All Features"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeatureComparisonMatrix;
